'use client'

import * as React from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useCreateTaxRate,
    useUpdateTaxRate,
    type TaxRateItem,
    type CreateTaxRateInput,
    type UpdateTaxRateInput,
} from '@/lib/api/taxRates'
import { EditableTaxRateRow, type TaxRateFormData } from './editable-tax-rate-row'
import { DeleteTaxRateModal } from './delete-tax-rate-modal'
import { BulkDeleteTaxRatesModal } from './bulk-delete-tax-rates-modal'

interface EditableTaxRatesTableProps {
    taxRates: TaxRateItem[]
    storeId: string
}

let nextTempId = 0
function createTempId() {
    nextTempId += 1
    return `__new_${nextTempId}`
}

export function EditableTaxRatesTable({ taxRates, storeId }: EditableTaxRatesTableProps) {
    const { t } = useTranslation('taxes')

    const [editingIds, setEditingIds] = React.useState<Set<string>>(new Set())
    const [newRowIds, setNewRowIds] = React.useState<string[]>([])
    const [savingIds, setSavingIds] = React.useState<Set<string>>(new Set())
    const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
    const [deletingTaxRateId, setDeletingTaxRateId] = React.useState<string | undefined>()
    const [showBulkDelete, setShowBulkDelete] = React.useState(false)

    const createMutation = useCreateTaxRate()
    const updateMutation = useUpdateTaxRate()

    const allRowIds = [...taxRates.map((r) => r.id), ...newRowIds]
    const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selectedIds.has(id))
    const someSelected = allRowIds.some((id) => selectedIds.has(id))

    const toggleSelectAll = (checked: boolean) => {
        setSelectedIds(checked ? new Set(allRowIds) : new Set())
    }

    const toggleSelect = (id: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (checked) next.add(id)
            else next.delete(id)
            return next
        })
    }

    const startEditing = (id: string) => {
        setEditingIds((prev) => new Set(prev).add(id))
    }

    const stopEditing = (id: string) => {
        setEditingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
        })
    }

    const insertRow = () => {
        const tempId = createTempId()
        setNewRowIds((prev) => [...prev, tempId])
        setEditingIds((prev) => new Set(prev).add(tempId))
    }

    const removeNewRow = (tempId: string) => {
        setNewRowIds((prev) => prev.filter((id) => id !== tempId))
        stopEditing(tempId)
    }

    const handleSaveExisting = async (taxRateId: string, data: TaxRateFormData) => {
        setSavingIds((prev) => new Set(prev).add(taxRateId))
        try {
            const input: UpdateTaxRateInput = {
                id: taxRateId,
                data: {
                    name: data.name,
                    rate: data.rate,
                    include: data.include,
                    applyToShipping: data.applyToShipping,
                    countryCodes: data.countryCodes,
                    stateCodes: data.stateCodes,
                },
            }
            await updateMutation.mutateAsync(input)
            stopEditing(taxRateId)
        } finally {
            setSavingIds((prev) => {
                const next = new Set(prev)
                next.delete(taxRateId)
                return next
            })
        }
    }

    const handleSaveNew = async (tempId: string, data: TaxRateFormData) => {
        setSavingIds((prev) => new Set(prev).add(tempId))
        try {
            const input: CreateTaxRateInput = {
                name: data.name,
                rate: data.rate,
                include: data.include,
                applyToShipping: data.applyToShipping,
                countryCodes: data.countryCodes,
                stateCodes: data.stateCodes,
                storeId,
            }
            await createMutation.mutateAsync(input)
            removeNewRow(tempId)
        } finally {
            setSavingIds((prev) => {
                const next = new Set(prev)
                next.delete(tempId)
                return next
            })
        }
    }

    const handleDeleteSuccess = () => {
        setSelectedIds(new Set())
    }

    const selectedCount = selectedIds.size

    return (
        <div className="space-y-2">
            {selectedCount > 0 && (
                <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                    <span className="text-sm text-muted-foreground">
                        {selectedCount} {t('table.selectRow')}
                    </span>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowBulkDelete(true)}
                    >
                        {t('deleteSelected')}
                    </Button>
                </div>
            )}

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={allSelected || (someSelected && 'indeterminate')}
                                    onCheckedChange={(checked) => toggleSelectAll(!!checked)}
                                />
                            </TableHead>
                            <TableHead>{t('table.columns.name')}</TableHead>
                            <TableHead className="w-24">{t('table.columns.rate')}</TableHead>
                            <TableHead className="w-24">{t('table.columns.included')}</TableHead>
                            <TableHead className="w-24">{t('table.columns.shipping')}</TableHead>
                            <TableHead>{t('table.columns.countries')}</TableHead>
                            <TableHead>{t('table.columns.states')}</TableHead>
                            <TableHead className="w-24">{t('table.columns.actions')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {taxRates.length === 0 && newRowIds.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                    {t('noTaxRates')}
                                </TableCell>
                            </TableRow>
                        )}

                        {taxRates.map((taxRate) => (
                            <EditableTaxRateRow
                                key={taxRate.id}
                                taxRate={taxRate}
                                isEditing={editingIds.has(taxRate.id)}
                                isSaving={savingIds.has(taxRate.id)}
                                selected={selectedIds.has(taxRate.id)}
                                onSelectChange={(checked) => toggleSelect(taxRate.id, checked)}
                                onEdit={() => startEditing(taxRate.id)}
                                onCancel={() => stopEditing(taxRate.id)}
                                onSave={(data) => handleSaveExisting(taxRate.id, data)}
                                onDelete={() => setDeletingTaxRateId(taxRate.id)}
                            />
                        ))}

                        {newRowIds.map((tempId) => (
                            <EditableTaxRateRow
                                key={tempId}
                                isEditing={true}
                                isSaving={savingIds.has(tempId)}
                                selected={selectedIds.has(tempId)}
                                onSelectChange={(checked) => toggleSelect(tempId, checked)}
                                onEdit={() => {}}
                                onCancel={() => removeNewRow(tempId)}
                                onSave={(data) => handleSaveNew(tempId, data)}
                                onDelete={() => removeNewRow(tempId)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Button variant="outline" size="sm" onClick={insertRow}>
                <Plus className="mr-2 h-4 w-4" />
                {t('insertRow')}
            </Button>

            <DeleteTaxRateModal
                taxRateId={deletingTaxRateId}
                onClose={() => setDeletingTaxRateId(undefined)}
                onSuccess={handleDeleteSuccess}
            />

            {showBulkDelete && (
                <BulkDeleteTaxRatesModal
                    taxRateIds={Array.from(selectedIds).filter((id) => !id.startsWith('__new_'))}
                    onClose={() => setShowBulkDelete(false)}
                    onSuccess={handleDeleteSuccess}
                />
            )}
        </div>
    )
}
